
// short stops - fuel, a delivery, lunch - play at their real length. they
// are part of what the trip was, and they cost little to sit through.
const IDLE_GAP_THRESHOLD_MS = 10 * 60 * 1000;

// How much of the run all the compressed gaps get between them.
//

const GAP_SHARE_OF_RUN = 0.12;

type ReplaySegment = {
  startClock: number;
  endClock: number;
  // screen time this segment is worth, in the same arbitrary units as
  // every other segment's - only the ratios matter
  weight: number;
  isGap: boolean;
};

export type ReplayTimeline = {
  start: number;
  end: number;
  // resolves a position in the run (0..1) to the real timestamp being drawn
  // at that point, plus whether the clock is currently racing through
  // compressed dead air
  at: (fraction: number) => { clock: number; isFastForward: boolean };
};

export const buildReplayTimeline = (
  timestamps: number[]
): ReplayTimeline | null => {
  const sorted = Array.from(new Set(timestamps)).sort((a, b) => a - b);
  if (sorted.length < 2) return null;

  const segments: ReplaySegment[] = [];
  // consecutive fixes closer together than the threshold are collapsed into
  // one active segment - linear within a segment either way, so this is the
  // same pacing at a fraction of the segment count
  let activeStart = sorted[0];
  let previous = sorted[0];

  const closeActiveSegment = (endClock: number) => {
    if (endClock <= activeStart) return;
    segments.push({
      startClock: activeStart,
      endClock,
      weight: endClock - activeStart,
      isGap: false,
    });
  };

  for (let index = 1; index < sorted.length; index++) {
    const timestamp = sorted[index];
    if (timestamp - previous > IDLE_GAP_THRESHOLD_MS) {
      closeActiveSegment(previous);
      segments.push({
        startClock: previous,
        endClock: timestamp,
        // weighted below, once the run's active total is known
        weight: 0,
        isGap: true,
      });
      activeStart = timestamp;
    }
    previous = timestamp;
  }
  closeActiveSegment(previous);

  const activeWeight = segments.reduce(
    (sum, segment) => sum + (segment.isGap ? 0 : segment.weight),
    0
  );
  if (activeWeight <= 0) return null;

  // the gap budget is what keeps GAP_SHARE_OF_RUN of the total, split
  // evenly, so more gaps means each is quicker rather than the run getting
  // longer. never stretch a gap past its real length - a twelve-minute stop
  // shouldn't be made to look like more of the day than it was.
  const gapSegments = segments.filter((segment) => segment.isGap);
  if (gapSegments.length > 0) {
    const budgetPerGap =
      (activeWeight * GAP_SHARE_OF_RUN) /
      ((1 - GAP_SHARE_OF_RUN) * gapSegments.length);
    gapSegments.forEach((segment) => {
      segment.weight = Math.min(
        segment.endClock - segment.startClock,
        budgetPerGap
      );
    });
  }

  const totalWeight = segments.reduce(
    (sum, segment) => sum + segment.weight,
    0
  );
  if (totalWeight <= 0) return null;

  const start = sorted[0];
  const end = sorted[sorted.length - 1];

  return {
    start,
    end,
    at: (fraction: number) => {
      let remaining = Math.min(1, Math.max(0, fraction)) * totalWeight;
      for (const segment of segments) {
        if (remaining <= segment.weight) {
          const within = segment.weight > 0 ? remaining / segment.weight : 1;
          return {
            clock:
              segment.startClock +
              (segment.endClock - segment.startClock) * within,
            isFastForward: segment.isGap,
          };
        }
        remaining -= segment.weight;
      }
      return { clock: end, isFastForward: false };
    },
  };
};
